/**
 * สรุปการอนุมัติ — รวม ลา/OT ที่อนุมัติแล้ว + มาสาย/ขาดงาน ไว้หน้าเดียว
 * ดูได้ 2 มุมมอง: รายเดือน (ทบทวนก่อนปิดรอบเงินเดือน — มีรายการรายวัน) และ
 * รายปี (ภาพรวมทั้งปี — สรุปแยกเดือน + รายคน, ไม่มีรายการรายวัน กดที่เดือนเพื่อเจาะดู).
 * ข้อมูล read-only — จัดการรายการมาสาย/ขาดได้ที่ "กิจกรรมผิดปกติ", อนุมัติคำขอที่ "อนุมัติคำขอ".
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useApprovalSummary, fetchApprovalSummaryDetail } from '../../hooks';
import { buildReportCsv } from '../../lib/approval-report';
import { useToast } from '../../components/ui/toast';
import { ChevronLeft, ChevronRight, Plane, Clock, AlertTriangle, UserX, ArrowRight, Download, X } from 'lucide-react';

function saveCsv(csv: string, filename: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function shiftMonth(month: string, delta: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(month: string): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  return new Date(y, m - 1, 1).toLocaleDateString('th-TH', { month: 'long', year: 'numeric' });
}
/** ชื่อเดือนสั้นสำหรับตารางรายปี เช่น "ม.ค." */
function monthShort(month: string): string {
  const m = Number(month.slice(5, 7));
  return new Date(2000, m - 1, 1).toLocaleDateString('th-TH', { month: 'short' });
}
/** ปี พ.ศ. */
const yearLabel = (year: number) => `ปี ${year + 543}`;
const thDay = (d: string) => new Date(d).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });
const dateRange = (from: string, to: string, days: number) =>
  from === to ? thDay(from) : `${thDay(from)} – ${thDay(to)} (${days} วัน)`;

export function ApprovalSummary() {
  const toast = useToast();
  const [mode, setMode] = useState<'month' | 'year'>('month');
  const [month, setMonth] = useState<string>(currentMonth());
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [focusEmp, setFocusEmp] = useState<string | null>(null); // กดคน → กรองรายละเอียดเฉพาะคนนั้น
  const [pos, setPos] = useState(''); // fb#492 — TN กรองรายงานตามตำแหน่งเป็นหลัก
  const [downloading, setDownloading] = useState(false);
  const isYear = mode === 'year';
  const period = isYear ? String(year) : month;
  const { data, isLoading } = useApprovalSummary(period);
  const isCurrent = isYear ? year === new Date().getFullYear() : month === currentMonth();

  // กดเดือนในตารางรายปี → เจาะดูรายละเอียดเดือนนั้น
  const drillToMonth = (mo: string) => {
    setMonth(mo);
    setFocusEmp(null);
    setMode('month');
  };

  // กรองรายละเอียดตามคน/ตำแหน่งที่เลือก (ถ้าไม่เลือก = ทุกคน)
  const focusName = focusEmp ? data?.by_emp.find((e) => e.emp_code === focusEmp)?.emp_name ?? null : null;
  const posOf = new Map((data?.by_emp ?? []).map((e) => [e.emp_code, e.position ?? '']));
  const positions = [...new Set((data?.by_emp ?? []).map((e) => e.position ?? '').filter(Boolean))].sort((a, b) => a.localeCompare(b, 'th'));
  const keep = <T extends { emp_code: string }>(arr: T[]) =>
    arr.filter((x) => (!focusEmp || x.emp_code === focusEmp) && (!pos || posOf.get(x.emp_code) === pos));

  // ดึง report — ไฟล์ตรงกับที่กรองบนหน้าจอ (เลือกคนไว้ = ได้เฉพาะคนนั้น).
  // รายปีต้องขอรายการรายวันเพิ่ม เพราะมุมมองปกติไม่ส่งมา (พันแถว โหลดหนัก)
  const handleDownload = async () => {
    if (!data || downloading) return;
    setDownloading(true);
    try {
      const full = isYear ? await fetchApprovalSummaryDetail(period) : data;
      const label = `${isYear ? `ทั้งปี ${year + 543}` : monthLabel(month)}${pos ? ` · ตำแหน่ง ${pos}` : ''}`;
      saveCsv(buildReportCsv(full, label, focusEmp, pos), `report-${period}${pos ? `-${pos}` : ''}.csv`);
    } catch {
      toast.error('ดึง report ไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setDownloading(false);
    }
  };

  // เลือกตำแหน่ง → การ์ดรวมเฉพาะตำแหน่งนั้น (รวมจากสรุปรายคน) ไม่ใช่ยอดทั้งบริษัท
  const s = data?.summary && pos ? (() => {
    const rows = data.by_emp.filter((e) => e.position === pos);
    const sum = (f: (e: (typeof rows)[number]) => number) => Math.round(rows.reduce((a, e) => a + (f(e) || 0), 0) * 100) / 100;
    return {
      // รายปีหน้าเว็บไม่มีรายการลารายใบ (by_emp มีแค่จำนวนวัน) → นับใบไม่ได้ การ์ดลาใช้จำนวนวันแทน
      leave: isYear ? null : data.leaves.filter((l) => posOf.get(l.emp_code) === pos).length, leave_days: sum((e) => e.leave_days),
      ot: sum((e) => e.ot_count), ot_hours: sum((e) => e.ot_hours),
      late: sum((e) => e.late_count), late_open: sum((e) => e.late_open),
      absent: sum((e) => e.absent_count), absent_open: sum((e) => e.absent_open),
    };
  })() : data?.summary;
  const cards = [
    s?.leave === null
      ? { key: 'leave', label: 'ลา', sub: 'วัน', value: s.leave_days, icon: Plane, tone: 'text-blue-600 bg-blue-50' }
      : { key: 'leave', label: 'ลา', sub: s ? `${s.leave_days} วัน` : '', value: s?.leave ?? 0, icon: Plane, tone: 'text-blue-600 bg-blue-50' },
    // โชว์ "ชั่วโมง" เป็นเลขหลัก (เลขที่ HR เอาไปใช้จริง) ส่วนจำนวนใบเป็นคำบรรยาย
    // ตอนยังโหลดไม่เสร็จห้ามขึ้น "0 ใบ" — เป็นคำกล่าวอ้างที่ยังไม่รู้ว่าจริงไหม
    { key: 'ot', label: 'OT', sub: s ? `ชั่วโมง (${s.ot} ใบ)` : 'ชั่วโมง', value: s?.ot_hours ?? 0, icon: Clock, tone: 'text-emerald-600 bg-emerald-50' },
    {
      key: 'late', label: 'มาสาย',
      sub: s ? (s.late_open > 0 ? `ครั้ง · ค้าง ${s.late_open}` : 'ครั้ง · จัดการครบแล้ว') : 'ครั้ง',
      value: s?.late ?? 0, icon: AlertTriangle, tone: 'text-amber-600 bg-amber-50',
    },
    {
      key: 'absent', label: 'ขาดงาน',
      sub: s ? (s.absent_open > 0 ? `ครั้ง · ค้าง ${s.absent_open}` : 'ครั้ง · จัดการครบแล้ว') : 'ครั้ง',
      value: s?.absent ?? 0, icon: UserX, tone: 'text-red-600 bg-red-50',
    },
  ];

  const empRows = (data?.by_emp ?? []).filter((e) => !pos || e.position === pos);
  const hasAny = (s?.leave ?? 0) + (s?.leave_days ?? 0) + (s?.ot ?? 0) + (s?.late ?? 0) + (s?.absent ?? 0) > 0;

  return (
    <AdminLayout title="สรุปการอนุมัติ">
      <p className="text-sm text-gray-600 mb-3">
        รวม "ลา / OT ที่อนุมัติแล้ว" และ "มาสาย / ขาดงาน" ไว้ที่เดียว —{' '}
        {isYear ? 'ภาพรวมทั้งปี เทียบกันได้ทีละเดือน' : 'ทบทวนก่อนปิดรอบเงินเดือน'}
      </p>
      {/* ⚠️ ต้องเขียนไว้ตรงนี้ ไม่ใช่แค่ในไฟล์ CSV — คนดูหน้าจอแล้วเอาเลขไปเทียบสลิปเลย */}
      <p className="text-xs text-gray-400 mb-3">
        ชั่วโมง OT ที่แสดง = ชั่วโมงรวมจากใบที่อนุมัติ ปกติตรงกับที่ระบบสรุปไว้ตอนคิดเงิน —
        ยกเว้นค่าทำงานวันหยุดที่ระบบคิดให้เองจากการสแกนโดยไม่ต้องมีใบ · คนโหมดเงินเดือนล้วนและรายชั่วโมง
        จะมีชั่วโมงขึ้นแต่ไม่ได้รับเงิน OT
      </p>

      {/* สลับมุมมอง รายเดือน / รายปี */}
      <div className="flex justify-center mb-3">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
          {([
            { key: 'month', label: 'รายเดือน' },
            { key: 'year', label: 'รายปี' },
          ] as const).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                setFocusEmp(null);
                setMode(t.key);
              }}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                mode === t.key ? 'bg-brand-600 text-white' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ตัวเลือกช่วงเวลา */}
      <div className="flex items-center justify-center gap-2 mb-4">
        <button
          type="button"
          onClick={() => (isYear ? setYear(year - 1) : setMonth(shiftMonth(month, -1)))}
          className="rounded-lg border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-50"
          aria-label={isYear ? 'ปีก่อนหน้า' : 'เดือนก่อนหน้า'}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="min-w-[150px] text-center">
          <span className="font-semibold text-sm">{isYear ? yearLabel(year) : monthLabel(month)}</span>
          {isCurrent && <span className="ml-1.5 text-[11px] text-brand-600">({isYear ? 'ปีนี้' : 'เดือนนี้'})</span>}
        </div>
        <button
          type="button"
          onClick={() => (isYear ? setYear(year + 1) : setMonth(shiftMonth(month, 1)))}
          disabled={isCurrent}
          className="rounded-lg border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
          aria-label={isYear ? 'ปีถัดไป' : 'เดือนถัดไป'}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {positions.length > 0 && (
        <div className="flex justify-center mb-4">
          <select
            className="input max-w-[260px]"
            value={pos}
            onChange={(e) => { setPos(e.target.value); setFocusEmp(null); }}
            aria-label="ตำแหน่ง"
          >
            <option value="">ทุกตำแหน่ง</option>
            {positions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      )}

      {/* การ์ดสรุป 4 ช่อง */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
        {cards.map((c) => (
          <div key={c.key} className="card p-3">
            <div className={`inline-flex items-center justify-center w-7 h-7 rounded-lg mb-2 ${c.tone}`}>
              <c.icon className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold leading-none">{isLoading ? '–' : c.value.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">{c.label}{c.sub ? ` · ${c.sub}` : ''}</p>
          </div>
        ))}
      </div>

      {/* กันเข้าใจผิดที่เจอจริง: HR กด "ปิด" การ์ดกิจกรรมผิดปกติแล้วคาดว่ายอดขาดงาน
          จะลด — แต่ปิด = เลิกเตือน ไม่ได้แปลว่าวันนั้นไม่ขาด */}
      {!isLoading && (s?.absent ?? 0) + (s?.late ?? 0) > 0 && (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          ตัวเลขมาสาย/ขาดงาน = เกิดขึ้นจริงกี่ครั้งใน{isYear ? 'ปี' : 'เดือน'}นี้ —
          การกด "ปิด" ที่หน้ากิจกรรมผิดปกติทำให้เลิกเตือนเท่านั้น ยอดนี้และยอดหักเงินไม่เปลี่ยน.
          ถ้าวันนั้นไม่ได้ขาดจริง ให้กด "มาทำงานปกติ" หรือ "บันทึกเป็นลา" ที่การ์ดนั้น
          แล้ว<strong>รันสรุปเวลาเข้างานใหม่</strong> ตัวเลขถึงจะเปลี่ยนตาม
        </p>
      )}

      {isLoading ? (
        <div className="card p-4 text-sm text-gray-400">กำลังโหลด…</div>
      ) : !hasAny ? (
        <div className="card p-6 text-center text-sm text-gray-400">
          {isYear ? 'ปีนี้' : 'เดือนนี้'}ยังไม่มีการอนุมัติลา/OT หรือมาสาย/ขาดงาน
        </div>
      ) : (
        <>
          {/* รายปี — สรุปแยกเดือน (กดเพื่อเจาะดูรายละเอียดเดือนนั้น) */}
          {/* แยกรายเดือนเป็นยอดทั้งบริษัท (ไม่มีแยกตำแหน่ง) → เลือกตำแหน่งแล้วซ่อน กันเลขไม่ตรงกับการ์ด/ตาราง */}
          {isYear && pos && (
            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
              ตาราง "แยกรายเดือน" แสดงเฉพาะตอนดูทุกตำแหน่ง — เลือก "ทุกตำแหน่ง" เพื่อดูยอดรายเดือน
            </p>
          )}
          {isYear && !pos && data?.by_month && (
            <section className="mb-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-2">แยกรายเดือน</h2>
              <p className="text-xs text-gray-400 mb-2">แตะที่เดือน เพื่อดูรายละเอียดของเดือนนั้น</p>
              <div className="card overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500">
                      <th className="text-left font-medium px-3 py-2">เดือน</th>
                      <th className="text-center font-medium px-2 py-2">ลา (วัน)</th>
                      <th className="text-center font-medium px-2 py-2">OT (ชม.)</th>
                      <th className="text-center font-medium px-2 py-2">สาย</th>
                      <th className="text-center font-medium px-2 py-2">ขาด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_month.map((mo) => (
                      <tr
                        key={mo.month}
                        onClick={() => drillToMonth(mo.month)}
                        className="border-t border-gray-100 cursor-pointer hover:bg-brand-50"
                      >
                        <td className="px-3 py-2 font-medium text-brand-700">{monthShort(mo.month)}</td>
                        <td className="text-center px-2 py-2">{mo.leave_days || <span className="text-gray-300">–</span>}</td>
                        <td className="text-center px-2 py-2">{mo.ot_hours || <span className="text-gray-300">–</span>}</td>
                        <td className="text-center px-2 py-2">{mo.late ? <span className="text-amber-600 font-medium">{mo.late}</span> : <span className="text-gray-300">–</span>}</td>
                        <td className="text-center px-2 py-2">{mo.absent ? <span className="text-red-600 font-medium">{mo.absent}</span> : <span className="text-gray-300">–</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* สรุปรายคน */}
          {empRows.length > 0 && (
            <section className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-gray-700">สรุปรายคน{isYear ? ' (ทั้งปี)' : ''}</h2>
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={downloading}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline disabled:opacity-50 disabled:cursor-wait"
                >
                  <Download className="w-4 h-4" /> {downloading ? 'กำลังดึง…' : 'ดึง report'}
                </button>
              </div>
              {!isYear && <p className="text-xs text-gray-400 mb-2">แตะที่ชื่อคน เพื่อดูว่าลา/ขาด/สาย วันไหนบ้าง</p>}
              <div className="card overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500">
                      <th className="text-left font-medium px-3 py-2">พนักงาน</th>
                      <th className="text-center font-medium px-2 py-2">ลา (วัน)</th>
                      <th className="text-center font-medium px-2 py-2">OT (ชม.)</th>
                      <th className="text-center font-medium px-2 py-2">สาย</th>
                      <th className="text-center font-medium px-2 py-2">ขาด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empRows.map((e) => (
                      <tr
                        key={e.emp_code}
                        onClick={isYear ? undefined : () => setFocusEmp((cur) => (cur === e.emp_code ? null : e.emp_code))}
                        className={`border-t border-gray-100 ${isYear ? '' : 'cursor-pointer hover:bg-brand-50'} ${focusEmp === e.emp_code ? 'bg-brand-50' : ''}`}
                      >
                        <td className="px-3 py-2 max-w-[180px]">
                          <p className={`truncate font-medium ${isYear ? '' : 'text-brand-700'}`}>{e.emp_name}</p>
                          {e.position && <p className="truncate text-xs text-gray-500">{e.position}</p>}
                        </td>
                        <td className="text-center px-2 py-2">{e.leave_days || <span className="text-gray-300">–</span>}</td>
                        <td className="text-center px-2 py-2">{e.ot_hours || <span className="text-gray-300">–</span>}</td>
                        <td className="text-center px-2 py-2">{e.late_count ? <span className="text-amber-600 font-medium">{e.late_count}</span> : <span className="text-gray-300">–</span>}</td>
                        <td className="text-center px-2 py-2">{e.absent_count ? <span className="text-red-600 font-medium">{e.absent_count}</span> : <span className="text-gray-300">–</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* รายปีแสดงยอดรวมอย่างเดียว — รายการรายวันทั้งปีเยอะเกินกว่าจะอ่านไหว */}
          {isYear && (
            <div className="card p-3 mb-4 bg-gray-50 text-xs text-gray-500">
              มุมมองรายปีแสดงเฉพาะยอดรวม — อยากรู้ว่าลา/ขาด/สาย วันไหนบ้าง ให้แตะที่เดือนในตาราง "แยกรายเดือน"
              หรือกด "ดึง report" เพื่อเอารายละเอียดรายวันทั้งปีไปเปิดใน Excel
            </div>
          )}

          {/* แถบบอกว่ากำลังกรองเฉพาะคน */}
          {focusEmp && (
            <div className="mb-3 flex items-center justify-between gap-2 rounded-lg bg-brand-50 border border-brand-100 px-3 py-2">
              <p className="text-sm text-brand-800">กำลังดูเฉพาะ <b>{focusName ?? focusEmp}</b></p>
              <button
                type="button"
                onClick={() => setFocusEmp(null)}
                className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline shrink-0"
              >
                <X className="w-3.5 h-3.5" /> ดูทุกคน
              </button>
            </div>
          )}

          {/* ลา — โชว์ว่าลาวันไหน + ชนิดสิทธิ์ที่ใช้ */}
          <ListSection icon={Plane} tone="text-blue-600" title="ลาที่อนุมัติแล้ว" count={keep(data!.leaves).length}>
            {keep(data!.leaves).map((l, i) => (
              <Row key={`l${i}`} name={l.emp_name} position={posOf.get(l.emp_code)} when={dateRange(l.date, l.end_date, l.days)} detail={l.reason}>
                <span className="badge-info shrink-0">{l.type_label}</span>
              </Row>
            ))}
          </ListSection>

          {/* OT */}
          <ListSection icon={Clock} tone="text-emerald-600" title="OT ที่อนุมัติแล้ว" count={keep(data!.ots).length}>
            {keep(data!.ots).map((o, i) => (
              <Row
                key={`o${i}`}
                name={o.emp_name} position={posOf.get(o.emp_code)}
                when={`${thDay(o.date)}${o.start_time && o.end_time ? ` · ${o.start_time.slice(0, 5)}–${o.end_time.slice(0, 5)}` : ''}${o.hours ? ` · ${o.hours} ชม.` : ''}`}
                detail={o.reason}
              >
                <span className="badge-info shrink-0">{o.type_label}</span>
              </Row>
            ))}
          </ListSection>

          {/* มาสาย — detail มีจำนวนนาทีที่สาย (วันไหน กี่นาที) */}
          <ListSection icon={AlertTriangle} tone="text-amber-600" title="มาสาย" count={keep(data!.late).length}>
            {keep(data!.late).map((e, i) => (
              <Row key={`la${i}`} name={e.emp_name} position={posOf.get(e.emp_code)} when={thDay(e.date)} detail={e.detail}>
                <StatusBadge resolved={e.resolved} />
              </Row>
            ))}
          </ListSection>

          {/* ขาดงาน — วันไหนบ้าง */}
          <ListSection icon={UserX} tone="text-red-600" title="ขาดงาน" count={keep(data!.absent).length}>
            {keep(data!.absent).map((e, i) => (
              <Row key={`ab${i}`} name={e.emp_name} position={posOf.get(e.emp_code)} when={thDay(e.date)} detail={e.detail}>
                <StatusBadge resolved={e.resolved} />
              </Row>
            ))}
          </ListSection>

          <div className="card p-3 mt-4 bg-gray-50 flex items-center justify-between gap-2">
            <p className="text-xs text-gray-500">จัดการรายการมาสาย/ขาดงาน (สั่งทับผล / บันทึกเป็นลา) ได้ที่หน้ากิจกรรมผิดปกติ</p>
            <Link to="/hr/escalations" className="text-sm font-medium text-brand-700 hover:underline shrink-0 flex items-center gap-1">
              ไปจัดการ <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

function ListSection({
  icon: Icon,
  tone,
  title,
  count,
  children,
}: {
  icon: typeof Plane;
  tone: string;
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  if (count === 0) return null;
  return (
    <section className="mb-5">
      <h2 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
        <Icon className={`w-4 h-4 ${tone}`} /> {title}
        <span className="text-xs font-normal text-gray-400">({count})</span>
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Row({ name, position, when, detail, children }: { name: string; position?: string; when: string; detail?: string | null; children?: React.ReactNode }) {
  return (
    <div className="card p-3 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-medium text-sm truncate">{name}{position && <span className="font-normal text-gray-500"> · {position}</span>}</p>
        <p className="text-xs text-gray-500">{when}</p>
        {detail && <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{detail}</p>}
      </div>
      {children}
    </div>
  );
}

function StatusBadge({ resolved }: { resolved: boolean }) {
  return resolved ? (
    <span className="badge-success shrink-0">จัดการแล้ว</span>
  ) : (
    <span className="badge-warn shrink-0">ค้าง</span>
  );
}
